import React, { useState } from 'react';
import { Button, useTheme, Box, Typography, styled, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import Input from 'components/Common/Input';
import { useGetBusinessProfile, useUpdateBusinessProfile } from 'apis/business/hook';
import Spinner from 'components/Common/Spinner';
import Select from 'components/Common/Select';
import { useGetCountries, useGetStates } from 'apis/country/hook';
import DataTable from 'components/Common/DataTable';
import { useUpdateUserStatus } from 'apis/user/hook';
import { useGetBusinesses, useUpdateBusiness } from 'apis/agency/hook';
import { ColumnDef } from '@tanstack/react-table';
import Pagination from 'components/Common/Pagination';
import { BusinessUserResponse } from 'apis/business/types';
import Switch from 'components/Common/Switch';
import TelInput from 'components/Common/TelInput';
import { Timezones } from 'utils/timezones';

export default function BusinessProfileForm({ agencyView }: { agencyView: Boolean }) {
  const theme = useTheme();
  const { data: businessProfile, isLoading } = useGetBusinessProfile();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    businessProfile?.avatar_url ? businessProfile?.avatar_url : null
  );
  const { mutateAsync: updateBusinessProfile } = useUpdateBusinessProfile();
  const { mutateAsync: updateBusiness } = useUpdateBusiness();

  const [currentPage, setCurrentPage] = useState(1);
  const { data: businesses } = useGetBusinesses(
    { page: currentPage, filter: { account_status_eq: true } },
    {
      queryKey: ['businesses', currentPage],
      enabled: Boolean(agencyView)
    }
  );
  const { mutateAsync: updateUserStatus } = useUpdateUserStatus();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const newUrl = URL.createObjectURL(file);
      setAvatarUrl(newUrl);
      const formData = new FormData();
      formData.append('business[avatar]', file);
      await updateBusinessProfile(formData);
    }
  };

  const { handleSubmit, control, watch } = useForm({
    values: {
      business: {
        ...businessProfile,
        address_attributes: businessProfile?.address
        // tts_voice_service: businessProfile?.tts_voice_service || '',
        // stt_voice_service: businessProfile?.stt_voice_service || ''
      }
    }
  });
  const businessCountry = watch('business.address_attributes.country');
  const accountStatus = watch('business.account_status');
  const { data: countries, isLoading: isCountriesLoading } = useGetCountries();
  const { data: states, isLoading: isStatesLoading } = useGetStates(businessCountry);

  const onSubmit = async (data: any) => {
    if (agencyView) {
      await updateBusiness(data);
    } else {
      await updateBusinessProfile(data);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Typography variant='h1' fontWeight={500}>
        Business Profile Settings
      </Typography>
      <Typography variant='body2' color='secondary'>
        {agencyView
          ? 'Manage business profile information and settings'
          : 'Manage your business profile information and settings'}
      </Typography>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
        <Grid container spacing='32px'>
          <Grid size={{ xs: 12, md: 6 }} bgcolor={theme.palette.background.paper} padding='24px' borderRadius='4px'>
            <Typography variant='h6' marginBottom={2}>
              General Information
            </Typography>

            <Box
              sx={{
                border: '1px solid #F3F3F3',
                borderRadius: '4px',
                padding: 2
              }}
            >
              <Typography variant='body1' fontWeight='500'>
                Business Logo
              </Typography>
              <Box
                display={{ xs: 'block', sm: 'flex' }}
                sx={{
                  gap: 2,
                  alignItems: 'center',
                  marginTop: '8px'
                }}
              >
                <Avatar
                  src={avatarUrl || '/path/to/default/avatar.png'}
                  alt='User Avatar'
                  sx={{ width: 64, height: 64 }}
                />
                <Box>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button component='label' role={undefined} variant='outlined' tabIndex={-1}>
                      Choose files
                      <VisuallyHiddenInput type='file' onChange={handleFileChange} multiple />
                    </Button>
                    <Typography variant='body2'>No file chosen</Typography>
                  </Box>
                  <Typography color='secondary' fontSize='12px'>
                    The proposed size is 350px*180px. No bigger than 2.5MB{' '}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Input
              name='business.name'
              label='Friendly Business Name'
              control={control}
              fullWidth
              sx={{ marginTop: 2 }}
            />

            <Input
              name='business.legal_name'
              label='Legal Business Name'
              control={control}
              fullWidth
              sx={{ marginTop: 2 }}
            />

            <Box>
              <Grid container spacing='24px' sx={{ marginTop: 2 }}>
                <Grid size={6}>
                  <Input name='business.email' label='Business Email' control={control} fullWidth />
                </Grid>
                <Grid size={6}>
                  <TelInput
                    name='business.phone'
                    label='Business Number'
                    control={control}
                    defaultCountry='US'
                    preferredCountries={['US', 'CA', 'IN']}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>

            <Input name='business.website' label='Business Website' control={control} fullWidth sx={{ marginY: 2 }} />
            {/* <Select
              name='business.niche'
              label='Business Niche'
              control={control}
              options={[{ value: 'Eye Care Centre', label: 'Eye Care Centre' }]}
            /> */}
            {/* {agencyView && (
              <Box my={2}>
                <Select
                  name='business.tts_voice_service'
                  label='TTS Voice Service'
                  control={control}
                  options={[
                    { value: 'elevenlabs', label: 'ElevenLabs' },
                    { value: 'azure', label: 'Azure' },
                    { value: 'openai', label: 'OpenAI' }
                  ]}
                />
              </Box>
            )} */}
            {/* {agencyView && (
              <Select
                name='business.stt_voice_service'
                label='STT Voice Service'
                control={control}
                options={[
                  { value: 'deepgram', label: 'Deepgram' },
                  { value: 'azure', label: 'Azure' }
                ]}
              />
            )} */}
            {agencyView && (
              <Box
                sx={{
                  borderTop: '1px solid rgb(216, 216, 216)',
                  marginTop: 3,
                  paddingTop: 2
                }}
              >
                <Typography variant='body1' fontWeight={500}>
                  Account Status
                </Typography>
                <Typography variant='body2' color='secondary' sx={{ marginBottom: 1 }}>
                  Enable or disable this business account. Disabled accounts cannot access the system.
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Switch name='business.account_status' control={control} />
                  <Typography
                    variant='body2'
                    sx={{
                      color: accountStatus ? theme.palette.primary.main : theme.palette.secondary.main,
                      fontWeight: 600
                    }}
                  >
                    {accountStatus ? 'Enabled' : 'Disabled'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Business Address */}
          <Grid borderRadius='4px' size={{ xs: 12, md: 6 }} bgcolor={theme.palette.background.paper} p={3}>
            <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              Business Address
            </Typography>

            <Input
              name='business.address_attributes.street_address'
              label='Street Address'
              control={control}
              fullWidth
              sx={{ marginTop: 2 }}
            />
            <Box sx={{ marginY: 2 }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Input name='business.address_attributes.city' label='City' control={control} fullWidth />
                </Grid>
                <Grid size={6}>
                  <Input
                    name='business.address_attributes.postal_code'
                    label='Postal/Zip Code'
                    control={control}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ marginY: 2 }}>
              <Select
                key={businessCountry}
                name='business.address_attributes.country'
                label='Country'
                isLoading={isCountriesLoading}
                control={control}
                options={countries || []}
              />
            </Box>
            <Box sx={{ marginY: 2 }}>
              <Select
                key={businessCountry}
                name='business.address_attributes.state'
                label='State / Prov / Region'
                isLoading={isStatesLoading}
                control={control}
                options={states || []}
              />
            </Box>
            <Box sx={{ marginY: 2 }}>
              <Select
                key={businessCountry}
                name='business.address_attributes.time_zone'
                label='Time Zone'
                control={control}
                options={Timezones}
              />
            </Box>
            {/* todo: currently functionally not using. */}
            {/* <Select
              name='business.address_attributes.platform_language'
              label='Platform Language'
              control={control}
              options={languageOptions}
            /> */}
          </Grid>
        </Grid>
        <Button
          color='primary'
          type='submit'
          variant='contained'
          sx={{ marginTop: '24px', marginLeft: 'auto', display: 'block' }}
        >
          Update
        </Button>
      </Box>
      {agencyView && (
        <Box sx={{ marginTop: '20px' }}>
          <DataTable
            data={businessProfile ? businessProfile.users : []}
            columns={columns(updateUserStatus)}
            isHeaderSticky={true}
            isLoading={isLoading}
          />
          <Pagination totalPages={businesses?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
        </Box>
      )}
    </>
  );
}

const columns = (
  updateUserStatus: (params: { id: number; payload: { profile: { status: 'active' | 'inactive' } } }) => Promise<any>
): ColumnDef<BusinessUserResponse>[] => [
  {
    header: 'Name',
    accessorKey: 'name'
  },
  {
    header: 'Email',
    accessorKey: 'email'
  }
  // {
  //   header: 'User Status',
  //   cell: (info) => {
  //     const user = info.row.original;
  //     const userId = user?.id;
  //     const isActive = user?.status === 'active';
  //     return (
  //       <Switch
  //         size='small'
  //         checked={isActive}
  //         onChange={async (event, checked) => {
  //           if (userId) {
  //             try {
  //               await updateUserStatus({
  //                 id: userId,
  //                 payload: { profile: { status: checked ? 'active' : 'inactive' } }
  //               });
  //             } catch (error) {
  //               console.error('Failed to update status:', error);
  //             }
  //           }
  //         }}
  //         disabled={!userId}
  //       />
  //     );
  //   }
  // }
];

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

//todo: currently functionally not using.
// const languageOptions = [
//   { value: 'en', label: 'English' },
//   { value: 'es', label: 'Spanish' },
//   { value: 'fr', label: 'French' },
//   { value: 'de', label: 'German' },
//   { value: 'zh', label: 'Chinese (Simplified)' },
//   { value: 'zh-TW', label: 'Chinese (Traditional)' },
//   { value: 'ja', label: 'Japanese' },
//   { value: 'ko', label: 'Korean' },
//   { value: 'ru', label: 'Russian' },
//   { value: 'pt-BR', label: 'Portuguese (Brazil)' },
//   { value: 'pt-PT', label: 'Portuguese (Portugal)' },
//   { value: 'it', label: 'Italian' },
//   { value: 'ar', label: 'Arabic' },
//   { value: 'nl', label: 'Dutch' },
//   { value: 'pl', label: 'Polish' },
//   { value: 'sv', label: 'Swedish' },
//   { value: 'da', label: 'Danish' },
//   { value: 'fi', label: 'Finnish' },
//   { value: 'no', label: 'Norwegian' },
//   { value: 'tr', label: 'Turkish' },
//   { value: 'el', label: 'Greek' },
//   { value: 'he', label: 'Hebrew' },
//   { value: 'hi', label: 'Hindi' }
// ];
